/**
 * Tiny localStorage-backed "Recently viewed" cache for the command palette.
 *
 * Storage shape: a single JSON-encoded array under `scwt_recent_visits`,
 * MRU-ordered, capped at MAX_PER_KIND entries per kind.
 *
 * Each entry: { kind, id, label, sublabel?, to, viewed_at }
 *
 * Records a visit synchronously on page-mount so the next palette open
 * surfaces it immediately. No network round-trip.
 */
const STORAGE_KEY = "scwt_recent_visits";
const MAX_PER_KIND = 5;
const TOTAL_CAP = 30; // hard cap to keep localStorage write small

function safeRead() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function safeWrite(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    /* quota / private-mode — silent no-op */
  }
}

/** Record (or refresh) a visit. Idempotent: same kind+id moves to head. */
export function recordVisit({ kind, id, label, sublabel, to }) {
  if (!kind || !id || !to || !label) return;
  const list = safeRead();
  const next = [
    { kind, id, label, sublabel, to, viewed_at: new Date().toISOString() },
    ...list.filter((x) => !(x.kind === kind && x.id === id)),
  ];

  // Cap per kind
  const byKind = {};
  const trimmed = [];
  for (const e of next) {
    byKind[e.kind] = (byKind[e.kind] || 0) + 1;
    if (byKind[e.kind] <= MAX_PER_KIND) trimmed.push(e);
  }
  safeWrite(trimmed.slice(0, TOTAL_CAP));
}

/** Return the most-recent visits, MRU-ordered. */
export function getRecentVisits(limit = 10) {
  return safeRead().slice(0, limit);
}

/** Wipe all recent visits (e.g. on logout). */
export function clearRecentVisits() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}
